import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Tracks the timestamp at which the user last "viewed" the
 * Lời mời / Yêu cầu surface (Notifications → tab Lời mời,
 * /invitation-management, /team-invitations, /club-invitations, etc.).
 *
 * Source of truth: column `profiles.invitations_last_seen_at` so the value
 * follows the user across devices. localStorage is kept only as a fast cache
 * for first paint to avoid a flash of red badge while the profile loads.
 *
 * Realtime subscription on the user's own profile row keeps every open tab
 * and other signed-in devices in sync within a couple of seconds of any
 * "mark as seen" action.
 */

const KEY_PREFIX = 'sportrank:invitations-seen:';
const MIGRATION_FLAG_PREFIX = 'sportrank:invitations-seen-migrated:';

function readCache(profileId: string): string | null {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(`${KEY_PREFIX}${profileId}`)
      : null;
  } catch {
    return null;
  }
}

function writeCache(profileId: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${KEY_PREFIX}${profileId}`;
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    /* ignore quota / privacy mode errors */
  }
}

function readMigrationFlag(profileId: string): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage.getItem(`${MIGRATION_FLAG_PREFIX}${profileId}`) === '1'
    );
  } catch {
    return false;
  }
}

function setMigrationFlag(profileId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${MIGRATION_FLAG_PREFIX}${profileId}`, '1');
  } catch {
    /* ignore */
  }
}

const queryKey = (profileId: string | null) =>
  ['invitations-last-seen', profileId] as const;

export function useInvitationsSeen() {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;
  const queryClient = useQueryClient();

  // Read from server (cached). Use localStorage as initialData so the first
  // render after a fresh load is instant and doesn't flash the badge.
  const { data: lastSeenIso } = useQuery({
    queryKey: queryKey(profileId),
    queryFn: async () => {
      if (!profileId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('invitations_last_seen_at')
        .eq('id', profileId)
        .maybeSingle();
      if (error) throw error;
      const value = (data?.invitations_last_seen_at as string | null) ?? null;
      writeCache(profileId, value);
      return value;
    },
    enabled: !!profileId,
    initialData: profileId ? () => readCache(profileId) : undefined,
    staleTime: 30_000,
  });

  // One-shot migration: if server has nothing yet but the device has a
  // legacy localStorage mark, push it up so the user doesn't lose their
  // previous "seen" state on the first sync.
  useEffect(() => {
    if (!profileId) return;
    if (lastSeenIso) return;
    if (readMigrationFlag(profileId)) return;
    const legacy = readCache(profileId);
    if (!legacy) {
      setMigrationFlag(profileId);
      return;
    }
    let cancelled = false;
    (async () => {
      const { error } = await supabase
        .from('profiles')
        .update({ invitations_last_seen_at: legacy })
        .eq('id', profileId);
      if (!cancelled && !error) {
        setMigrationFlag(profileId);
        queryClient.setQueryData(queryKey(profileId), legacy);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId, lastSeenIso, queryClient]);

  // Realtime: keep every device/tab in sync when one of them marks seen.
  useEffect(() => {
    if (!profileId) return;
    const channel = supabase
      .channel(`profile-invitations-seen-${profileId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${profileId}`,
        },
        (payload) => {
          const next =
            (payload.new as { invitations_last_seen_at?: string | null } | null)
              ?.invitations_last_seen_at ?? null;
          writeCache(profileId, next);
          queryClient.setQueryData(queryKey(profileId), next);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profileId, queryClient]);

  const lastSeenAt = useMemo<Date | null>(
    () => (lastSeenIso ? new Date(lastSeenIso) : null),
    [lastSeenIso]
  );

  const markSeen = useCallback(async () => {
    if (!profileId) return;
    const previous = queryClient.getQueryData<string | null>(queryKey(profileId)) ?? null;
    const nowIso = new Date().toISOString();

    // Optimistic: update cache + localStorage instantly.
    queryClient.setQueryData(queryKey(profileId), nowIso);
    writeCache(profileId, nowIso);

    const { error } = await supabase
      .from('profiles')
      .update({ invitations_last_seen_at: nowIso })
      .eq('id', profileId);

    if (error) {
      // Roll back so the badge state stays truthful.
      queryClient.setQueryData(queryKey(profileId), previous);
      writeCache(profileId, previous);
    }
  }, [profileId, queryClient]);

  const isUnseen = useCallback(
    (createdAt: string | null | undefined): boolean => {
      if (!createdAt) return false;
      if (!lastSeenAt) return true;
      return new Date(createdAt).getTime() > lastSeenAt.getTime();
    },
    [lastSeenAt]
  );

  return { lastSeenAt, markSeen, isUnseen };
}
